import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Empty,
  Input,
  message,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import cx from 'classnames';
import { Link, useHistory } from 'react-router-dom';

import { ReactComponent as GridViewIcon } from '../../../assets/svgs/grid-view-icon.svg';
import { ReactComponent as TableViewIcon } from '../../../assets/svgs/list-view-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchPlanograms,
  searchPlanogramsByName,
} from '../../../store/features/planogram/planogramSlice';
import { debounce, DebouncedFunction, UserPermissions } from '../../../util';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import Button from '../../elements/Button';
import NoResultsFound from '../../elements/NoResultsFound';
import ViewWrapper from '../../elements/ViewWrapper';

import { useAuth } from '../../../hooks/useAuth';
import NoPermission from '../../elements/NoPermission';
import './PlanogramProjectsView.less';
import PlanogramProjectItemsList, {
  PlanogramItemsListViewType,
} from './components/PlanogramProjectItemsList/PlanogramProjectItemsList';

const { Title } = Typography;

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;
const planogram2ListViewTypeKey = 'planogram-list-view-type';
const lastPlanogram2ProjectsItemLengthKey =
  'last-planogram-projects-item-length-key';

const PlanogramProjectsView = () => {
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [clearingSearch, setClearingSearch] = useState(false);
  const [viewType, setViewType] = useState<PlanogramItemsListViewType>(
    (localStorage.getItem(
      planogram2ListViewTypeKey
    ) as PlanogramItemsListViewType) || PlanogramItemsListViewType.Grid
  );
  const [pagination, setPagination] = useState({ offset: 0, limit: 20 });

  const {
    value: projects,
    fetchingPlanograms,
    searchingPlanograms,
    totalCount,
  } = useAppSelector((state) => state.planogram);

  const hasNoSearchResultsFound = useMemo(
    () => !fetchingPlanograms && !!searchQuery && projects.length === 0,
    [fetchingPlanograms, searchQuery, projects.length]
  );

  const hasProjects = !(!fetchingPlanograms && projects.length === 0);

  const hasNoProjects = useMemo(
    () => !fetchingPlanograms && projects.length === 0 && !clearingSearch,
    [fetchingPlanograms, projects.length, clearingSearch]
  );

  const hasMoreProjects =
    hasProjects && pagination?.offset! + pagination?.limit! < totalCount!;

  const isPermittedToLoadMore =
    !fetchingPlanograms &&
    !searchingPlanograms &&
    !searchQuery &&
    hasMoreProjects;

  const fetchProjects = useCallback(async () => {
    try {
      await dispatch(fetchPlanograms()).unwrap();
    } catch (error) {
      message.error('Failed to fetch planogram projects');
    }
  }, [dispatch]);

  useEffect(() => {
    if (!searchQuery) {
      fetchProjects();
    }
  }, [fetchProjects, searchQuery]);

  const createNewProjectButton = useMemo(
    (): JSX.Element => (
      <Link to={DrawerHashRoute.PlanogramForm}>
        <Button type="primary" data-cy="create-new-planogram-project-btn">
          Create New Project
          <PlusOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const uploadRSSegmentButton = useMemo(
    (): JSX.Element => (
      <Link to={DrawerHashRoute.RSSegmentUploadForm}>
        <Button type="primary" data-cy="upload-rs-segment-btn">
          Upload RS Segment
          <UploadOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string, clearing: boolean) => {
        if (clearing) {
          setClearingSearch(true);
        } else {
          setClearingSearch(false);
        }
        try {
          await dispatch(searchPlanogramsByName(query)).unwrap();
        } catch (error) {
          message.error('Failed to search planogram projects');
        }
      }, 700) as DebouncedFunction,
    [dispatch]
  );

  const updateSearchQuery = useCallback(
    (query: string) => {
      setSearchQuery(query);
      debouncedSearch(query, query === '');
    },
    [debouncedSearch]
  );

  const updatePlanogram2ListViewType = useCallback(
    (type: PlanogramItemsListViewType) => {
      setViewType(type);
      localStorage.setItem(planogram2ListViewTypeKey, type);
    },
    []
  );

  const headerSectionRight = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal" size="middle" align="center">
        <Tooltip title="Grid view" mouseEnterDelay={0.2} mouseLeaveDelay={0}>
          <div
            className={cx('planogram-view-type-icon', {
              selected: viewType === PlanogramItemsListViewType.Grid,
            })}
            onClick={() =>
              updatePlanogram2ListViewType(PlanogramItemsListViewType.Grid)
            }
          >
            <GridViewIcon color="#000" />
          </div>
        </Tooltip>
        <Tooltip title="Table view" mouseEnterDelay={0.2} mouseLeaveDelay={0}>
          <div
            className={cx('planogram-view-type-icon', {
              selected: viewType === PlanogramItemsListViewType.Table,
            })}
            onClick={() =>
              updatePlanogram2ListViewType(PlanogramItemsListViewType.Table)
            }
          >
            <TableViewIcon />
          </div>
        </Tooltip>
        <Input
          allowClear={!searchingPlanograms}
          value={searchQuery}
          onChange={(e) => updateSearchQuery(e.target.value)}
          placeholder="Search for a project..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={searchingPlanograms && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.PlanogramProjectsCreate) && (
          <>
            {uploadRSSegmentButton}
            {createNewProjectButton}
          </>
        )}
      </Space>
    ),
    [
      viewType,
      searchingPlanograms,
      searchQuery,
      createNewProjectButton,
      uploadRSSegmentButton,
      updatePlanogram2ListViewType,
      updateSearchQuery,
      hasPermission,
    ]
  );

  useEffect(() => {
    if (!!searchQuery || projects.length > 20) return;

    localStorage.setItem(
      lastPlanogram2ProjectsItemLengthKey,
      `${projects.length}`
    );
  }, [projects.length, searchQuery]);

  const handleProjectClick = (projectId: string) => {
    history.push(`/planogram/${projectId}`);
  };

  const renderContent = () => {
    if (searchingPlanograms) {
      return (
        <div className="custom-fields-view-container">
          <Spin indicator={antSpinIcon} />
        </div>
      );
    }

    if (!hasNoProjects) {
      return (
        <PlanogramProjectItemsList
          skeletonItemLength={
            +localStorage.getItem(
              lastPlanogram2ProjectsItemLengthKey
            )! as number
          }
          viewType={viewType}
          loading={fetchingPlanograms}
          projects={projects}
          onLoadMore={() => {
            setPagination((prev) => ({
              ...prev,
              offset: prev.offset + prev.limit,
            }));
          }}
          hasMore={isPermittedToLoadMore}
          onItemClick={handleProjectClick}
        />
      );
    }

    if (hasNoSearchResultsFound) {
      return (
        <NoResultsFound
          searchQuery={searchQuery}
          onClear={() => updateSearchQuery('')}
        />
      );
    }

    if (!hasMoreProjects) {
      return (
        <div className="custom-fields-view-container">
          <Empty
            description="Currently no planogram projects."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {createNewProjectButton}
          </Empty>
        </div>
      );
    }
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  if (!hasPermission(UserPermissions.PlanogramProjectsView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Projects</Title>
          <Badge
            size="default"
            count={totalCount}
            className="total-count-badge"
            overflowCount={999}
          />
        </Space>
      }
      headerSectionRight={headerSectionRight}
    >
      {renderContent()}
    </ViewWrapper>
  );
};

export default PlanogramProjectsView;
